/**
 * Button.tsx - Skinnr-themed Button component
 * 
 * CHANGE HISTORY:
 * - 251105_1516 [pending] danielbeer: Initial creation for ORCHESTRATOR-FIRST UI layer
 * 
 * A themeable button component that uses Skinnr's CSS variables for consistent styling.
 * Supports variants, sizes, icons, and disabled states.
 */

import React from 'react';
import './Button.css';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button variant */
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  /** Button size */
  size?: 'sm' | 'md' | 'lg';
  /** Icon to display (emoji or icon font) */
  icon?: string;
  /** Show icon only (no text) */
  iconOnly?: boolean;
  /** Active state (for toggle buttons) */
  active?: boolean;
  /** Full width button */
  fullWidth?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'secondary',
  size = 'md',
  icon,
  iconOnly = false,
  active = false,
  fullWidth = false,
  className = '',
  disabled = false,
  ...props
}) => {
  const classes = [
    'skinnr-button',
    `skinnr-button--${variant}`,
    `skinnr-button--${size}`,
    active && 'skinnr-button--active',
    iconOnly && 'skinnr-button--icon-only',
    fullWidth && 'skinnr-button--full-width',
    className
  ].filter(Boolean).join(' ');

  return (
    <button
      className={classes}
      disabled={disabled}
      {...props}
    >
      {icon && <span className="skinnr-button__icon">{icon}</span>}
      {!iconOnly && children && <span className="skinnr-button__text">{children}</span>}
    </button>
  );
};

export default Button;
